import { useState, useRef, useEffect } from 'react'
import PersonSmallPic from './PersonSmallPic'
import { PersonLiteDto } from '../api/person/models/PersonDto'
import { MapExtra } from '../utils/MapExtra'

interface Props {
    ids: string[]
    personCache: MapExtra<string, PersonLiteDto>
    avatarSizePx?: number // default 32
    overlapPx?: number // default 8
}

export function PersonUiMicroList({
    ids,
    personCache,
    avatarSizePx = 32,
    overlapPx = 8,
}: Props) {
    const containerRef = useRef<HTMLDivElement>(null)
    const [visibleCount, setVisibleCount] = useState(ids.length)

    useEffect(() => {
        if (!containerRef.current) return

        const RO = new ResizeObserver(() => {
            const container = containerRef.current
            if (!container) return
            const containerWidth = containerRef.current!.clientWidth
            // effective width per avatar (minus overlap)
            const slot = avatarSizePx - overlapPx
            // how many full slots fit, plus one extra overlap back
            const max = Math.floor((containerWidth + overlapPx) / slot)
            setVisibleCount(Math.min(ids.length, Math.max(0, max)))
        })

        RO.observe(containerRef.current)
        return () => RO.disconnect()
    }, [ids.length, avatarSizePx, overlapPx])

    const extra = ids.length - visibleCount

    return (
        <div
            ref={containerRef}
            className="flex items-center -space-x-2 overflow-hidden mb-2"
        >
            {ids.slice(0, visibleCount).map((id) => (
                <PersonSmallPic
                    key={id}
                    person={personCache.get(id)!}
                    size={`${avatarSizePx}px`}
                />
            ))}

            {extra > 0 && (
                <div
                    className="flex items-center justify-center rounded-full text-xs font-medium"
                    style={{
                        width: avatarSizePx,
                        height: avatarSizePx,
                        marginLeft: -overlapPx,
                        backgroundColor: '#E5E7EB', // Tailwind gray-200
                        color: '#6B7280', // Tailwind gray-500
                    }}
                >
                    +{extra}
                </div>
            )}
        </div>
    )
}
